/**
 * BookCover component for displaying book cover images.
 *
 * Features:
 * - Native lazy loading for performance
 * - Graceful fallback when image fails to load or doesn't exist
 * - Smooth fade-in animation on load
 * - Responsive aspect ratio (3:4 by default)
 * - Dark mode support
 * - Accessible with proper alt text handling
 */

import { useState, useCallback } from "react";
import { recordTypeIcon } from "@/lib/recordTypes";

interface BookCoverProps {
  /** URL of the cover image (from Open Library or similar) */
  coverUrl: string | null;
  /** Alternative text for the image */
  alt?: string;
  /** Document type for fallback icon */
  type?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES = {
  sm: "h-16 w-12", // List items
  md: "h-48 w-36", // Grid cards
  lg: "aspect-[3/4] w-full", // Detail page
} as const;

const ICON_SIZES = {
  sm: "text-2xl",
  md: "text-4xl",
  lg: "text-6xl",
} as const;

export function BookCover({
  coverUrl,
  alt = "",
  type = "book",
  size = "md",
  className = "",
}: BookCoverProps) {
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(
    coverUrl ? "loading" : "error"
  );

  const handleLoad = useCallback(() => {
    setImageState("loaded");
  }, []);

  const handleError = useCallback(() => {
    setImageState("error");
  }, []);

  const sizeClass = SIZE_CLASSES[size];
  const iconSize = ICON_SIZES[size];

  // If no cover URL, show fallback immediately
  if (!coverUrl || imageState === "error") {
    return (
      <div
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 dark:border-gray-700 dark:from-gray-800 dark:to-gray-900 ${sizeClass} ${className}`}
        role="img"
        aria-label={alt || "Couverture non disponible"}
      >
        <span className={`${iconSize} text-gray-400 dark:text-gray-500`} aria-hidden="true">
          {recordTypeIcon(type)}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 ${sizeClass} ${className}`}
    >
      {/* Loading skeleton */}
      {imageState === "loading" && (
        <div
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800"
          aria-hidden="true"
        />
      )}

      {/* Actual image */}
      <img
        src={coverUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`h-full w-full object-cover transition-opacity duration-300 ${
          imageState === "loaded" ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

export default BookCover;
